import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Button } from 'antd';
import { getContentCSS, injectStyleIntoHTML, wrapSnippetAsFullPage } from '../templateStyles';

const { Title, Paragraph } = Typography;

const VERSION_COLORS = {
  sales: { emoji: '🔥', color: '#e74c3c' },
  marketing: { emoji: '📢', color: '#3498db' },
  event: { emoji: '🎉', color: '#2ecc71' },
  paid_training: { emoji: '🎓', color: '#9b59b6' },
  free_training: { emoji: '🆓', color: '#2ecc71' },
  satisfaction: { emoji: '⭐', color: '#faad14' },
  needs: { emoji: '📋', color: '#9b59b6' },
  seo: { emoji: '🔍', color: '#3498db' },
  social: { emoji: '📱', color: '#2ecc71' },
};

const VERSION_LABEL_KEYS = {
  sales: 'sections.aiContent.sales',
  marketing: 'sections.aiContent.marketing',
  event: 'sections.aiContent.event',
  paid_training: 'sections.aiContent.paidTraining',
  free_training: 'sections.aiContent.freeTraining',
  satisfaction: 'sections.aiContent.satisfaction',
  needs: 'sections.aiContent.needs',
  seo: 'sections.aiContent.seo',
  social: 'sections.aiContent.social',
};

function AutoResizeIframe({ srcDoc, title, style }) {
  const ref = useRef(null);
  const [height, setHeight] = useState(500);

  const updateHeight = useCallback(() => {
    try {
      const doc = ref.current?.contentDocument || ref.current?.contentWindow?.document;
      if (doc?.body) {
        const h = doc.body.scrollHeight;
        if (h > 50) setHeight(h + 32);
      }
    } catch { /* cross-origin */ }
  }, []);

  useEffect(() => {
    const iframe = ref.current;
    if (!iframe) return;
    iframe.addEventListener('load', updateHeight);
    const id = setInterval(updateHeight, 1000);
    return () => { iframe.removeEventListener('load', updateHeight); clearInterval(id); };
  }, [updateHeight]);

  return (
    <iframe
      ref={ref}
      srcDoc={srcDoc}
      title={title}
      style={{ ...style, height, transition: 'height 0.3s ease' }}
    />
  );
}

export default function SectionAIContent({ section, siteInfo }) {
  const { t } = useTranslation();
  const data = section.content_data || {};
  const versions = data.versions || {};
  const versionKeys = Object.keys(versions);
  const [activeVersion, setActiveVersion] = useState(versionKeys[0] || '');

  const brandColor = siteInfo?.brand_color || '#1890ff';
  const template = siteInfo?.site_template || 'modern';
  const isMinimal = template === 'minimal';
  const isClassic = template === 'classic';

  const cardRadius = isMinimal ? 0 : isClassic ? 4 : 8;
  const cardBorder = '1px solid #e8e8e8';
  const cardShadow = isMinimal ? 'none' : isClassic ? '0 1px 4px rgba(0,0,0,0.08)' : '0 2px 8px rgba(0,0,0,0.06)';

  const cssTag = useMemo(() => getContentCSS(template, brandColor), [template, brandColor]);

  const rawHtml = activeVersion
    ? versions[activeVersion]?.html_body || ''
    : data.html_content || '';

  const styledHtml = useMemo(() => {
    if (!rawHtml) return '';
    const isFullPage = rawHtml.trim().startsWith('<!') || rawHtml.trim().toLowerCase().startsWith('<html');
    if (isFullPage) return injectStyleIntoHTML(rawHtml, cssTag);
    return wrapSnippetAsFullPage(rawHtml, cssTag);
  }, [rawHtml, cssTag]);

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: '48px 24px' }}>
      <Title level={2} style={{
        textAlign: isMinimal ? 'left' : 'center',
        marginBottom: 8,
        fontWeight: isMinimal ? 300 : 600,
        letterSpacing: isMinimal ? 1 : 0,
        borderBottom: isMinimal ? `2px solid ${brandColor}` : 'none',
        paddingBottom: isMinimal ? 8 : 0,
        display: isMinimal ? 'inline-block' : 'block',
      }}>
        {section.title}
      </Title>
      {section.subtitle && (
        <Paragraph style={{
          textAlign: isMinimal ? 'left' : 'center',
          color: '#666',
          marginBottom: 24,
        }}>
          {section.subtitle}
        </Paragraph>
      )}

      {versionKeys.length > 1 && (
        <div style={{
          display: 'flex',
          justifyContent: isMinimal ? 'flex-start' : 'center',
          gap: 8,
          marginBottom: 24,
          flexWrap: 'wrap',
        }}>
          {versionKeys.map(k => {
            const colors = VERSION_COLORS[k] || { emoji: '📄', color: '#666' };
            const label = VERSION_LABEL_KEYS[k] ? t(VERSION_LABEL_KEYS[k]) : k;
            const active = k === activeVersion;
            return (
              <Button
                key={k}
                type={active ? 'primary' : 'default'}
                onClick={() => setActiveVersion(k)}
                style={{
                  ...(active ? { background: colors.color, borderColor: colors.color } : {}),
                  borderRadius: isMinimal ? 0 : isClassic ? 4 : undefined,
                }}
              >
                {colors.emoji} {label}
              </Button>
            );
          })}
        </div>
      )}

      {styledHtml && (
        <AutoResizeIframe
          srcDoc={styledHtml}
          title={section.title}
          style={{
            width: '100%',
            border: cardBorder,
            borderRadius: cardRadius,
            boxShadow: cardShadow,
          }}
        />
      )}
    </div>
  );
}
